// S2-0f.2: Job stream hook — POST to create job, then GET SSE stream for progress
// Two-step: create job → connect to SSE stream → parse events → complete
import { useState, useRef, useCallback } from 'react';
import { apiPost, apiFetch } from '../utils/api';

export type JobStatus = 'idle' | 'creating' | 'streaming' | 'complete' | 'error';

export interface JobProgress {
  current_product?: number;
  total_products?: number;
  product_name?: string;
  product_id?: string;
  checkpoint?: string;
  percent?: number;
  stage?: string;
  message?: string;
}

export interface JobResult {
  type: string;
  config_id?: string;
  agent_id?: string;
  total_evaluations?: number;
  quality_avg?: number;
  results?: Record<string, unknown>[];
  [key: string]: unknown;
}

interface UseJobStreamReturn {
  jobId: string | null;
  status: JobStatus;
  progress: JobProgress | null;
  progressHistory: JobProgress[];
  result: JobResult | null;
  error: string | null;
  start: (path: string, body: unknown) => void;
  stop: () => void;
}

const API_BASE = import.meta.env.VITE_API_URL || '';
const API_KEY = import.meta.env.VITE_API_KEY || '';

/**
 * Hook for the two-step eval job flow:
 * 1. POST /eval/run (or /eval/readiness) → { job_id }
 * 2. GET /eval/jobs/:jobId/stream → SSE progress events
 * 3. On complete → fetch GET /eval/jobs/:jobId for full result
 */
export function useJobStream(): UseJobStreamReturn {
  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<JobStatus>('idle');
  const [progress, setProgress] = useState<JobProgress | null>(null);
  const [progressHistory, setProgressHistory] = useState<JobProgress[]>([]);
  const [result, setResult] = useState<JobResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const start = useCallback(async (path: string, body: unknown) => {
    // Reset state
    setJobId(null);
    setStatus('creating');
    setProgress(null);
    setProgressHistory([]);
    setResult(null);
    setError(null);

    try {
      // Step 1: Create the job
      console.log('[useJobStream] Step 1: Creating job at', path, body);
      const { job_id } = await apiPost<{ job_id: string }>(path, body);
      console.log('[useJobStream] Step 1 OK: job_id =', job_id);
      setJobId(job_id);
      setStatus('streaming');

      // Step 2: Connect to SSE stream
      const abortController = new AbortController();
      abortRef.current = abortController;

      const streamUrl = `${API_BASE}/v1/eval/jobs/${job_id}/stream`;
      console.log('[useJobStream] Step 2: Connecting to SSE at', streamUrl);

      const response = await fetch(streamUrl, {
        headers: {
          Accept: 'text/event-stream',
          ...(API_KEY ? { Authorization: `Bearer ${API_KEY}` } : {}),
        },
        mode: 'cors',
        signal: abortController.signal,
      });
      console.log('[useJobStream] Step 2 OK: status', response.status);

      if (!response.ok) {
        throw new Error(`Stream failed: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('No response body for streaming');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const part of parts) {
          if (!part.trim()) continue;

          let eventType = 'message';
          let eventData = '';

          for (const line of part.split('\n')) {
            if (line.startsWith('event: ')) eventType = line.slice(7).trim();
            else if (line.startsWith('data: ')) eventData = line.slice(6);
          }

          if (!eventData) continue;

          try {
            const parsed = JSON.parse(eventData);

            if (eventType === 'progress') {
              setProgress(parsed);
              setProgressHistory(prev => [...prev, parsed]);
            } else if (eventType === 'complete') {
              // Fetch full job result
              const fullJob = await apiFetch<{ result: JobResult }>(`/v1/eval/jobs/${job_id}`);
              setResult(fullJob.result);
              setStatus('complete');
              return;
            } else if (eventType === 'error') {
              setError(parsed.message || 'Job failed');
              setStatus('error');
              return;
            }
          } catch {
            // Non-JSON event — skip
          }
        }
      }

      // Stream ended without complete event — fetch job to check final state
      const finalJob = await apiFetch<{ status: string; result: JobResult; error?: string }>(`/v1/eval/jobs/${job_id}`);
      if (finalJob.status === 'complete') {
        setResult(finalJob.result);
        setStatus('complete');
      } else if (finalJob.status === 'failed') {
        setError(finalJob.error || 'Job failed');
        setStatus('error');
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return; // User cancelled
      const errMsg = err instanceof Error ? err.message : String(err);
      console.error('[useJobStream] FAILED:', errMsg, err);
      setError(errMsg);
      setStatus('error');
    }
  }, []);

  return { jobId, status, progress, progressHistory, result, error, start, stop };
}
